import { takeLatest, put, call, fork, select } from 'redux-saga/effects';
import { Action, AsyncActionCreators, Success } from 'typescript-fsa';
import { apiGetActions, IAPIDataGetParam } from '@app/react/action';
import { fetchCartItemAction, FetchCartItemParam, FetchCartItemResult } from '@app/react/action/fetchCartItem';
import { safe } from '@app/react/saga/common/safe';
import { api } from '@app/react/common/api';
import {
    ArrowType,
    OrderType,
    DesignSet,
    PartsProduct,
    OrdermadeArrowPartsName,
    GetResponseOrderParts,
    GetResponseFamilyCrestColors,
} from '@app/react/const/type';
import type { AppState } from '@app/react/reducer';
import { conv, convArrowType, convOrderType } from './conv';

type FetchAPIRT = { result: GetResponseOrderParts, error: Response };

const fetchAPI = (
    kind: OrdermadeArrowPartsName,
    arrowType: ArrowType,
    orderType: OrderType,
    selectItems: DesignSet,
) => {
    const selectedItems = conv(selectItems);
    const json = JSON.stringify(selectedItems);
    const at = convArrowType(arrowType);
    const ot = convOrderType(orderType);

    /*
     * query parameterの一部をjson形式にしているため、型エラーが出ます
     */
    return api.orderMadeFunctionGetOrderPartsGet({
        kind,
        arrowType: at,
        orderType: ot,
        selectedItems: json,
    });
};

type FetchFCColorAPIRT = { result: GetResponseFamilyCrestColors, error: Response };

const fetchFCColorAPI = (feather: PartsProduct | null) => {
    const featherId = feather?.id || -1;
    return api.orderMadeFunctionGetFamilycrestColorsGet({
        featherId,
    });
};

function* fetchOrderParts(
    kind: OrdermadeArrowPartsName,
    arrowType: ArrowType,
    orderType: OrderType,
    selectItems: DesignSet,
    action: Action<any>,
    actionCreator: AsyncActionCreators<any, any, any>,
) {
    const { result, error }: FetchAPIRT = yield safe(
        call(fetchAPI, kind, arrowType, orderType, selectItems),
    );
    if (error !== null) {
        console.error('[saga] fetch feather error:', error);
        yield put(
            actionCreator.failed({
                params: action.payload,
                error: {
                    message: error.message,
                },
            }),
        );
        return;
    }

    yield put(
        actionCreator.done({
            params: action.payload,
            result: {
                param: result,
            },
        }),
    );
}

function* fetchShaft(action: Action<IAPIDataGetParam>) {
    const { arrowType, orderType, selectItems } = action.payload;

    const kind = OrdermadeArrowPartsName.Shaft;
    yield call(
        fetchOrderParts,
        kind,
        arrowType,
        orderType,
        selectItems,
        action,
        apiGetActions.shaft,
    );
}

function* fetchFeather(action: Action<IAPIDataGetParam>) {
    const { arrowType, orderType, selectItems } = action.payload;

    const kind = OrdermadeArrowPartsName.Feather;
    yield call(
        fetchOrderParts,
        kind,
        arrowType,
        orderType,
        selectItems,
        action,
        apiGetActions.feather,
    );
}

function* fetchThread(action: Action<IAPIDataGetParam>) {
    const { arrowType, orderType, selectItems } = action.payload;

    const kind = OrdermadeArrowPartsName.Thread;
    yield call(
        fetchOrderParts,
        kind,
        arrowType,
        orderType,
        selectItems,
        action,
        apiGetActions.thread,
    );
}

function* fetchKebiki(action: Action<IAPIDataGetParam>) {
    const { arrowType, orderType, selectItems } = action.payload;

    const kind = OrdermadeArrowPartsName.Kebiki;
    yield call(
        fetchOrderParts,
        kind,
        arrowType,
        orderType,
        selectItems,
        action,
        apiGetActions.kebiki,
    );
}

function* fetchNock(action: Action<IAPIDataGetParam>) {
    const { arrowType, orderType, selectItems } = action.payload;

    const kind = OrdermadeArrowPartsName.Nock;
    yield call(
        fetchOrderParts,
        kind,
        arrowType,
        orderType,
        selectItems,
        action,
        apiGetActions.nock,
    );
}

function* fetchArrowhead(action: Action<IAPIDataGetParam>) {
    const { arrowType, orderType, selectItems } = action.payload;

    const kind = OrdermadeArrowPartsName.Arrowhead;
    yield call(
        fetchOrderParts,
        kind,
        arrowType,
        orderType,
        selectItems,
        action,
        apiGetActions.arrowhead,
    );
}

function* fetchArrowlength(action: Action<IAPIDataGetParam>) {
    const { arrowType, orderType, selectItems } = action.payload;

    const kind = OrdermadeArrowPartsName.Arrowlength;
    yield call(
        fetchOrderParts,
        kind,
        arrowType,
        orderType,
        selectItems,
        action,
        apiGetActions.arrowlength,
    );
}

function* fetchUnit(action: Action<IAPIDataGetParam>) {
    const { arrowType, orderType, selectItems } = action.payload;

    const kind = OrdermadeArrowPartsName.Unit;
    yield call(
        fetchOrderParts,
        kind,
        arrowType,
        orderType,
        selectItems,
        action,
        apiGetActions.unit,
    );
}

function* fetchArrowinsert(action: Action<IAPIDataGetParam>) {
    const { arrowType, orderType, selectItems } = action.payload;

    const kind = OrdermadeArrowPartsName.Arrowinsert;
    yield call(
        fetchOrderParts,
        kind,
        arrowType,
        orderType,
        selectItems,
        action,
        apiGetActions.arrowinsert,
    );
}

function* fetchTwine(action: Action<IAPIDataGetParam>) {
    const { arrowType, orderType, selectItems } = action.payload;

    const kind = OrdermadeArrowPartsName.Twine;
    yield call(
        fetchOrderParts,
        kind,
        arrowType,
        orderType,
        selectItems,
        action,
        apiGetActions.twine,
    );
}

function* fetchLame(action: Action<IAPIDataGetParam>) {
    const { arrowType, orderType, selectItems } = action.payload;

    const kind = OrdermadeArrowPartsName.Lame;
    yield call(
        fetchOrderParts,
        kind,
        arrowType,
        orderType,
        selectItems,
        action,
        apiGetActions.lame,
    );
}

function* fetchCarving(action: Action<IAPIDataGetParam>) {
    const { arrowType, orderType, selectItems } = action.payload;

    const kind = OrdermadeArrowPartsName.Carving;
    yield call(
        fetchOrderParts,
        kind,
        arrowType,
        orderType,
        selectItems,
        action,
        apiGetActions.carving,
    );
}

function* fetchFamilycrest(action: Action<IAPIDataGetParam>) {
    const { arrowType, orderType, selectItems } = action.payload;

    const kind = OrdermadeArrowPartsName.Familycrest;
    const { result: familycrest, error }: FetchAPIRT = yield safe(
        call(fetchAPI, kind, arrowType, orderType, selectItems),
    );
    if (error !== null) {
        console.error('[saga] fetch familycrest error:', error);
        yield put(
            apiGetActions.familycrest.failed({
                params: action.payload,
                error: {
                    message: error.statusText,
                },
            }),
        );
        return;
    }

    const { result: familycrestColor, error: error2 }: FetchFCColorAPIRT = yield safe(
        call(fetchFCColorAPI, selectItems.feather),
    );
    if (error2 !== null) {
        console.error('[saga] fetch familycrest-color error:', error2);
        yield put(
            apiGetActions.familycrest.failed({
                params: action.payload,
                error: {
                    message: error2.statusText,
                },
            }),
        );
        return;
    }

    yield put(
        apiGetActions.familycrest.done({
            params: action.payload,
            result: {
                familycrest,
                familycrestColor,
            },
        }),
    );
}

function* fetchTypezero(action: Action<IAPIDataGetParam>) {
    const { arrowType, orderType, selectItems } = action.payload;

    const kind = OrdermadeArrowPartsName.Typezero;
    yield call(
        fetchOrderParts,
        kind,
        arrowType,
        orderType,
        selectItems,
        action,
        apiGetActions.typezero,
    );
}

function* fetchDecoration(action: Action<IAPIDataGetParam>) {
    const { arrowType, orderType, selectItems } = action.payload;

    const kind = OrdermadeArrowPartsName.Decoration;
    yield call(
        fetchOrderParts,
        kind,
        arrowType,
        orderType,
        selectItems,
        action,
        apiGetActions.decoration,
    );
}

function* fetchFeatherinside(action: Action<IAPIDataGetParam>) {
    const { arrowType, orderType, selectItems } = action.payload;

    const kind = OrdermadeArrowPartsName.Featherinside;
    yield call(
        fetchOrderParts,
        kind,
        arrowType,
        orderType,
        selectItems,
        action,
        apiGetActions.featherinside,
    );
}

function* editcartFetchProducts(action: Action<Success<FetchCartItemParam, FetchCartItemResult>>) {
    const { arrowType, orderType } = action.payload.params;
    const selectItems = yield select((state: AppState) => state.customize);
    const params = { type: 'dummy', payload: { arrowType, orderType, selectItems } };

    yield fork(fetchFamilycrest, params);
}

export const fetchArrowProductSaga = [
    takeLatest(apiGetActions.shaft.started.type, fetchShaft),
    takeLatest(apiGetActions.feather.started.type, fetchFeather),
    takeLatest(apiGetActions.thread.started.type, fetchThread),
    takeLatest(apiGetActions.kebiki.started.type, fetchKebiki),
    takeLatest(apiGetActions.nock.started.type, fetchNock),
    takeLatest(apiGetActions.arrowhead.started.type, fetchArrowhead),
    takeLatest(apiGetActions.arrowlength.started.type, fetchArrowlength),
    takeLatest(apiGetActions.unit.started.type, fetchUnit),
    takeLatest(apiGetActions.arrowinsert.started.type, fetchArrowinsert),
    takeLatest(apiGetActions.twine.started.type, fetchTwine),
    takeLatest(apiGetActions.lame.started.type, fetchLame),
    takeLatest(apiGetActions.carving.started.type, fetchCarving),
    takeLatest(apiGetActions.familycrest.started.type, fetchFamilycrest),
    takeLatest(apiGetActions.typezero.started.type, fetchTypezero),
    takeLatest(apiGetActions.decoration.started.type, fetchDecoration),
    takeLatest(apiGetActions.featherinside.started.type, fetchFeatherinside),
    takeLatest(fetchCartItemAction.done.type, editcartFetchProducts),
];
